import React, { useEffect, useRef, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Navbar from "@/components/Navbar";
import VisionAiOptInBanner from "@/components/VisionAiOptInBanner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { safeHtml } from "@/lib/safeHtml";
import { useAuth } from "@/contexts/AuthContext";
import { compressImageForUpload } from "@/lib/imageCompression";
import SalesQuoteButton from "@/components/SalesQuoteButton";
import StickyGenerateBar from "@/components/StickyGenerateBar";
import { trackEvent } from "@/lib/track";
import ProductCodePickerDialog from "@/components/ProductCodePickerDialog";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Code2 } from "lucide-react";
import { toast } from "sonner";

// ── Page-specific sub-components live next to this file under
// `components/DashboardSimulate/` so that this page stays a thin orchestrator.
// Each child receives only the state/callbacks it needs (no Context, no Redux).
import UploadStep from "@/components/DashboardSimulate/UploadStep";
import ClientInfoStep from "@/components/DashboardSimulate/ClientInfoStep";
import StyleStep from "@/components/DashboardSimulate/StyleStep";
import CatalogSection from "@/components/DashboardSimulate/CatalogSection";
import FinalActionBar from "@/components/DashboardSimulate/FinalActionBar";
import RenderBriefEditor from "@/components/RenderBriefEditor";

export default function DashboardSimulate({ simType }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const fileRef = useRef(null);

  // ── upload + AI precheck state
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [lowResWarning, setLowResWarning] = useState(false);
  const [precheck, setPrecheck] = useState(null);
  const [prechecking, setPrechecking] = useState(false);

  // ── style / mode state
  const [style, setStyle] = useState(null);
  const [loading, setLoading] = useState(false);
  const [usage, setUsage] = useState(null);
  const [client, setClient] = useState({ name: "", phone: "", email: "" });
  const [catalog, setCatalog] = useState(null);
  const [coverage, setCoverage] = useState(null);
  const [selectedCats, setSelectedCats] = useState([]);

  // iter 64 — pick-by-code (feedback 22.05): sales/partner/admin can hand-pick
  // specific FDP product codes. simple_user never touches this — UI hidden.
  const [pickedCodes, setPickedCodes] = useState([]);
  const [codePickerOpen, setCodePickerOpen] = useState(false);

  // Simple users: allow them to pick categories too (max 5, must pick style
  // first). Default they keep AI-random behaviour by toggling this off.
  const [aiPickRandom, setAiPickRandom] = useState(true);

  // ── role flags
  const isPartner = user && user.role === "partner";
  const isSales = user && user.role === "sales";
  const isPartnerOrSales = isPartner || isSales;
  const isAdmin = user && user.role === "admin";
  const isSimple = user && user.role === "simple_user";

  // Sales has Auto/Manual toggle. Partner is always manual. Simple is always
  // auto-style.
  const [salesMode, setSalesMode] = useState("MANUAL");
  const [variationCount, setVariationCount] = useState(5);

  // Sprint 2 Vision AI — per-render brief (must_keep / must_remove / mood /
  // notes). Only shown to partner/sales/admin; ignored by backend for simple.
  const [renderBrief, setRenderBrief] = useState({
    must_keep: [],
    must_remove: [],
    mood: [],
    notes: "",
  });

  // Engine routing: INTERIOR uses FLUX_KONTEXT (better spatial preservation
  // for doors/windows in raw construction photos — ~90% geometric fidelity
  // vs ~70% with GPT_IMAGE_1). EXTERIOR keeps GPT_IMAGE_1 (better at facade
  // plaster work, ornaments, smaller architectural details).
  // Requires FAL_KEY credit. Monitor balance at fal.ai/dashboard.
  const engine = simType === "INTERIOR" ? "FLUX_KONTEXT" : "GPT_IMAGE_1";
  const [renderMode, setRenderMode] = useState("STANDARD");

  // Catalog picker rules:
  //   • Partner / Sales (manual mode): ALWAYS shown, no style gate
  //   • Simple user: shown only after a style is picked AND aiPickRandom=false
  //   • Admin: always shown
  const usingCatalogPicker =
    isPartner ||
    isAdmin ||
    (isSales && salesMode === "MANUAL") ||
    (isSimple && !aiPickRandom && !!style);
  const maxCats = isSimple ? 5 : isSales ? 10 : 10;

  const isInterior = simType === "INTERIOR";

  // EMPTY_ROOM render-mode only applies to INTERIOR. When the user navigates
  // from /dashboard/interior to /dashboard/exterior while EMPTY_ROOM is
  // selected, we silently clamp the effective value to STANDARD at the
  // call-site instead of touching state in a useEffect (which would
  // violate react-hooks/set-state-in-effect). The picker UI also filters
  // EMPTY_ROOM out for exterior, so the user never sees an invalid choice.
  const effectiveRenderMode =
    simType === "EXTERIOR" && renderMode === "EMPTY_ROOM" ? "STANDARD" : renderMode;

  useEffect(() => {
    api
      .get("/simulations/usage/today")
      .then((r) => setUsage(r.data))
      .catch(() => {});
    api
      .get(`/catalog?sim_type=${simType}`)
      .then((r) => setCatalog(r.data))
      .catch(() => {});
    api
      .get("/catalog/style-coverage")
      .then((r) => setCoverage(r.data))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simType]);

  const toggleCat = (slug) => {
    // Block selection of empty categories — AI cannot generate without
    // photos. When the user has picked a style, we count only photos for
    // that style, because that's what the AI will actually reference
    // downstream.
    const cat = catalog?.categories?.find((c) => c.slug === slug);
    const count = cat
      ? style && cat.photos_by_style
        ? cat.photos_by_style[style] || 0
        : cat.uploaded_photos_count || 0
      : 0;
    if (cat && count === 0 && !selectedCats.includes(slug)) {
      toast.error(t("simulate.category_empty_warning"), { duration: 6000 });
      return;
    }
    setSelectedCats((prev) => {
      if (prev.includes(slug)) return prev.filter((s) => s !== slug);
      if (prev.length >= maxCats) {
        toast.error(t("simulate.category_max_warning", { max: maxCats }));
        return prev;
      }
      return [...prev, slug];
    });
  };

  // Build a human-friendly error message from an axios failure.
  // We try (in order): server detail → status code → network/timeout hint.
  const explainUploadError = (e, fallback) => {
    const detail = formatApiErrorDetail(e?.response?.data?.detail);
    if (detail) return detail;
    const status = e?.response?.status;
    if (status === 413) return t("simulate.toast_image_too_large");
    if (status === 502 || status === 504) return t("simulate.toast_server_slow");
    if (status === 401) return t("simulate.toast_session_expired");
    if (status === 403) return t("simulate.toast_no_permission");
    const code = e?.code;
    if (code === "ECONNABORTED" || /timeout/i.test(e?.message || "")) {
      return t("simulate.toast_slow_network");
    }
    // iter 84 (09.06.2026 production feedback): user kept seeing "Conexiune
    // întreruptă" on Android while their connection was clearly fine —
    // the actual root cause was the image POST being too large for the
    // production reverse-proxy. The new wording is honest about the most
    // likely cause without blaming the user's connection.
    if (e?.message === "Network Error" || e?.code === "ERR_NETWORK") {
      return t("simulate.toast_upload_failed");
    }
    return fallback;
  };

  // Mobile-friendly upload with ADAPTIVE compression + retry. Production
  // feedback (09.06.2026 + 18.06.2026): some Android users still hit the
  // "Network Error" wall even at 720px/q65 because their photo arrived as
  // a 12-25 MB HEIC file (Xiaomi MIUI "High-efficiency image format"). iter
  // 92 adds upstream HEIC→JPEG conversion in compressImageForUpload AND a
  // 4th ultra-small retry tier that fits through any plausible proxy:
  //   • Attempt 1 — default (1280px / q80)         → typically ~500-900 KB
  //   • Attempt 2 — medium  (960px / q75)          → typically ~250-450 KB
  //   • Attempt 3 — small   (720px / q65)          → typically ~150-250 KB
  //   • Attempt 4 — last resort (480px / q50)      → typically ~50-90 KB
  //
  // Each retry only runs when the failure looks network-related (no HTTP
  // response, ECONNABORTED, etc.) — application-level errors (4xx/5xx HTTP)
  // are surfaced immediately to the user.
  const postFileWithAdaptiveRetry = async (url, rawFile, extraFields = {}) => {
    const profiles = [
      { maxDim: 1280, quality: 0.8 },   // default
      { maxDim: 960,  quality: 0.75 },  // first retry — half the pixels
      { maxDim: 720,  quality: 0.65 },  // smaller — readable
      { maxDim: 480,  quality: 0.5 },   // last resort — tiny, still readable for the AI precheck
    ];
    let lastErr = null;
    for (let attempt = 0; attempt < profiles.length; attempt += 1) {
      try {
        const compressed = await compressImageForUpload(rawFile, profiles[attempt]);
        const fd = new FormData();
        fd.append("file", compressed);
        for (const [k, v] of Object.entries(extraFields)) {
          fd.append(k, v);
        }
        if (attempt > 0) {
          // eslint-disable-next-line no-console
          console.warn(
            `[upload] retry ${attempt}/${profiles.length - 1} — ${compressed.size} bytes after recompress (${profiles[attempt].maxDim}px, q${profiles[attempt].quality})`,
          );
          toast.info(t("simulate.toast_retrying", { attempt: attempt + 1 }), {
            duration: 2500,
          });
        }
        return await api.post(url, fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
      } catch (e) {
        lastErr = e;
        const isNetwork =
          e?.message === "Network Error" ||
          e?.code === "ECONNABORTED" ||
          e?.code === "ERR_NETWORK";
        // For HTTP application errors (4xx/5xx) we DON'T retry — the user
        // needs to know about them (413 too large, 403 forbidden, etc.).
        if (!isNetwork || attempt === profiles.length - 1) throw e;
        // Brief pause before recompressing — gives a flaky mobile network a
        // chance to recover before we hit it with a smaller payload.
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, 800 + attempt * 400));
      }
    }
    throw lastErr;
  };

  const runPrecheck = async () => {
    if (!file) return;
    setPrechecking(true);
    setPrecheck(null);
    try {
      const { data } = await postFileWithAdaptiveRetry(
        "/simulations/precheck",
        file,
        { sim_type: simType },
      );
      setPrecheck(data);
      if (data.proceed_recommended) {
        toast.success(t("simulate.toast_precheck_ok"));
      } else {
        toast.warning(t("simulate.toast_precheck_warn"));
      }
    } catch (e) {
      toast.error(explainUploadError(e, t("simulate.toast_precheck_failed")));
    } finally {
      setPrechecking(false);
    }
  };

  const onFileChange = (selected) => {
    if (!selected) return;
    // Lenient MIME check — Android Chrome sometimes sends
    // `application/octet-stream` for HEIC/Samsung One UI camera shots.
    // Accept anything starting with "image/" OR with a recognized extension.
    const name = (selected.name || "").toLowerCase();
    const allowedExts = [".jpg", ".jpeg", ".png", ".webp", ".heic", ".heif", ".jfif", ".bmp"];
    const hasImageMime = (selected.type || "").startsWith("image/");
    const hasImageExt = allowedExts.some((e) => name.endsWith(e));
    if (!hasImageMime && !hasImageExt) {
      toast.error(t("simulate.toast_invalid_image_type"));
      return;
    }
    if (selected.size > 30 * 1024 * 1024) {
      toast.error(t("simulate.toast_image_oversize_30"));
      return;
    }
    setFile(selected);
    setLowResWarning(false);
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target.result);
      // Detect low-resolution sources — they limit AI fidelity
      const img = new window.Image();
      img.onload = () => {
        if (Math.max(img.width, img.height) < 800) setLowResWarning(true);
      };
      img.src = e.target.result;
    };
    reader.readAsDataURL(selected);
  };

  const onDrop = (e) => {
    e.preventDefault();
    onFileChange(e.dataTransfer.files?.[0]);
  };

  const onRemoveFile = () => {
    setFile(null);
    setPreview(null);
    setLowResWarning(false);
    setPrecheck(null);
  };

  const onClientPick = (c) =>
    setClient({
      name: c.name || "",
      email: c.email || "",
      phone: c.phone || "",
    });

  const submit = async () => {
    if (!file) return toast.error(t("simulate.toast_upload_first"));
    if (!style) return toast.error(t("simulate.toast_pick_style"));
    if (isPartnerOrSales) {
      if (!client.name || !client.phone || !client.email) {
        return toast.error(t("simulate.toast_client_required"));
      }
      if (usingCatalogPicker && selectedCats.length === 0) {
        return toast.error(t("simulate.toast_category_required"));
      }
    }
    if (isSimple && !aiPickRandom && selectedCats.length === 0) {
      return toast.error(t("simulate.toast_simple_cat_or_auto"));
    }
    if (isSimple && !aiPickRandom && selectedCats.length > 5) {
      return toast.error(t("simulate.toast_simple_max_5"));
    }
    setLoading(true);
    // Build all the form fields EXCEPT the file (which the adaptive retry
    // helper recompresses per attempt). The helper takes care of the multipart
    // FormData assembly so we just pass key/value strings.
    const fields = {
      sim_type: simType,
      style: style,
      count: String(variationCount),
      engine: engine,
      render_mode: effectiveRenderMode,
    };
    if (isPartnerOrSales) {
      fields.client_name = client.name;
      fields.client_phone = client.phone;
      fields.client_email = client.email;
      if (usingCatalogPicker) {
        fields.selected_categories = selectedCats.join(",");
      }
    }
    // Simple users: optional category picker (max 5, style-gated). When
    // aiPickRandom=true, omit selected_categories and let the backend pick 3
    // random categories from the style's catalog bucket.
    if (isSimple) {
      if (!aiPickRandom && selectedCats.length > 0) {
        fields.selected_categories = selectedCats.join(",");
      }
      if (aiPickRandom) fields.ai_pick_random = "true";
    }
    // Admin: catalog picker is always usable
    if (isAdmin && usingCatalogPicker && selectedCats.length > 0) {
      fields.selected_categories = selectedCats.join(",");
    }
    if (pickedCodes.length > 0 && (isPartnerOrSales || isAdmin)) {
      fields.selected_product_codes = pickedCodes.join(",");
    }
    // Sprint 2 Vision AI — attach the per-render brief as a compact JSON
    // string (backend parses + sanitizes). Only for roles that see the UI.
    if (isPartnerOrSales || isAdmin) {
      const hasBriefContent =
        (renderBrief.must_keep && renderBrief.must_keep.length > 0) ||
        (renderBrief.must_remove && renderBrief.must_remove.length > 0) ||
        (renderBrief.mood && renderBrief.mood.length > 0) ||
        (renderBrief.notes && renderBrief.notes.trim());
      if (hasBriefContent) {
        fields.render_brief = JSON.stringify({
          must_keep: renderBrief.must_keep || [],
          must_remove: renderBrief.must_remove || [],
          mood: renderBrief.mood || [],
          notes: (renderBrief.notes || "").trim() || null,
        });
      }
    }
    try {
      trackEvent("render.start", {
        sim_type: simType,
        style,
        categories_count: (fields.selected_categories || []).length,
      });
      const { data } = await postFileWithAdaptiveRetry(
        "/simulations",
        file,
        fields,
      );
      // Inform the user when one or more selected categories were
      // auto-dropped because they had zero FDP photos for the chosen style.
      // The backend still generates the requested N renders, using only the
      // populated categories, but the user should know which got skipped.
      const dropped = data.categories_dropped || [];
      if (dropped.length > 0 && catalog?.categories) {
        const cat_lookup = Object.fromEntries(
          catalog.categories.map((c) => [c.slug, c.name]),
        );
        const names = dropped.map((s) => cat_lookup[s] || s).join(", ");
        toast.warning(
          t("simulate.toast_dropped_categories", {
            n: dropped.length,
            style: style.replace("_", " "),
            names,
          }),
          { duration: 7000 },
        );
      }
      toast.success(
        t("simulate.success_started_count", { count: variationCount }),
      );
      trackEvent("render.submitted", {
        sim_id: data.id,
        sim_type: simType,
        style,
        variations: variationCount,
      });
      navigate(`/simulation/${data.id}`);
    } catch (e) {
      trackEvent("render.error", { sim_type: simType, style });
      toast.error(explainUploadError(e, t("simulate.toast_sim_start_error")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="min-h-screen bg-background"
      data-testid={`dashboard-${simType.toLowerCase()}`}
    >
      <Navbar />
      <main className="max-w-7xl mx-auto px-6 lg:px-10 pt-10 pb-20">
        {/* Sprint 1 Vision AI — opt-in banner (partner/sales/admin only) */}
        <VisionAiOptInBanner testId="vision-optin-banner-simulate" />
        <div className="flex items-center justify-between mb-10">
          <div>
            <Link
              to="/dashboard"
              className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-orange-400 transition mb-4"
              data-testid="back-to-selector"
            >
              <ArrowLeft size={14} /> {t("dashboard.back_to_home")}
            </Link>
            <div className="text-xs uppercase tracking-[0.3em] text-orange-500 mb-2">
              {t("nav.dashboard")} {isInterior ? "01" : "02"}
            </div>
            <h1 className="font-outfit font-black text-white text-5xl md:text-6xl tracking-tighter leading-none">
              {isInterior
                ? t("dashboard.card_interior_title").toUpperCase()
                : t("dashboard.card_exterior_title").toUpperCase()}
            </h1>
            <p className="text-zinc-400 mt-3 max-w-xl">
              {isPartnerOrSales
                ? t("simulate.desc_partner_sales")
                : isInterior
                ? t("simulate.desc_interior")
                : t("simulate.desc_exterior")}
            </p>
            {isInterior && !isPartnerOrSales && (
              <div
                className="mt-5 max-w-xl bg-orange-500/5 border border-orange-500/20 rounded-md p-4"
                data-testid="furniture-info-banner"
              >
                <div className="text-xs uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                  {t("simulate.how_ai_works")}
                </div>
                <p
                  className="text-sm text-zinc-300 leading-relaxed"
                  dangerouslySetInnerHTML={safeHtml(t("simulate.interior_info_banner"))}
                />
              </div>
            )}
            {!isInterior && !isPartnerOrSales && (
              <div
                className="mt-5 max-w-xl bg-orange-500/5 border border-orange-500/20 rounded-md p-4"
                data-testid="exterior-info-banner"
              >
                <div className="text-xs uppercase tracking-[0.25em] text-orange-500 font-bold mb-2">
                  {t("simulate.how_ai_works")}
                </div>
                <p
                  className="text-sm text-zinc-300 leading-relaxed"
                  dangerouslySetInnerHTML={safeHtml(t("simulate.exterior_info_banner"))}
                />
              </div>
            )}
          </div>

          {usage && (
            <div className="hidden md:flex flex-col gap-2 items-end">
              <div
                className="border border-white/10 rounded-lg px-5 py-3 bg-zinc-900/40"
                data-testid="dashboard-usage-badge"
              >
                <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">
                  {t("simulate.daily_limit")}
                </div>
                <div className="font-outfit text-2xl font-black text-white mt-1">
                  {usage.used}
                  <span className="text-zinc-600">/{usage.limit}</span>
                </div>
              </div>
              <SalesQuoteButton
                variant="outline"
                size="sm"
                testId="sales-quote-btn-simulate-header"
              />
            </div>
          )}
        </div>

        {/* Sales Auto/Manual toggle */}
        {isSales && (
          <div
            className="mb-8 border border-white/10 rounded-lg bg-zinc-900/30 p-1 inline-flex items-center gap-1"
            data-testid="sales-mode-toggle"
          >
            <button
              type="button"
              onClick={() => {
                setSalesMode("AUTO");
                setSelectedCats([]);
              }}
              className={`px-5 py-2 rounded-md text-sm font-medium uppercase tracking-[0.15em] transition-all ${
                salesMode === "AUTO"
                  ? "bg-orange-500 text-white"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid="sales-mode-auto"
            >
              {t("simulate.sales_mode_auto")}
            </button>
            <button
              type="button"
              onClick={() => setSalesMode("MANUAL")}
              className={`px-5 py-2 rounded-md text-sm font-medium uppercase tracking-[0.15em] transition-all ${
                salesMode === "MANUAL"
                  ? "bg-orange-500 text-white"
                  : "text-zinc-400 hover:text-white"
              }`}
              data-testid="sales-mode-manual"
            >
              {t("simulate.sales_mode_manual")}
            </button>
          </div>
        )}

        <div className="grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <UploadStep
              isInterior={isInterior}
              fileRef={fileRef}
              preview={preview}
              onFileChange={onFileChange}
              onDrop={onDrop}
              onRemove={onRemoveFile}
              lowResWarning={lowResWarning}
              precheck={precheck}
              prechecking={prechecking}
              onPrecheck={runPrecheck}
            />

            {isPartnerOrSales && (
              <ClientInfoStep
                client={client}
                onChange={setClient}
                onPick={onClientPick}
              />
            )}
          </div>

          <div className="lg:col-span-5">
            <StyleStep
              simType={simType}
              style={style}
              onStyleChange={setStyle}
              coverage={coverage}
              selectedCats={selectedCats}
              isSimple={isSimple}
              aiPickRandom={aiPickRandom}
              onAiPickRandomChange={setAiPickRandom}
              renderMode={effectiveRenderMode}
              onRenderModeChange={setRenderMode}
            />
          </div>
        </div>

        <CatalogSection
          simType={simType}
          isInterior={isInterior}
          isSales={isSales}
          salesMode={salesMode}
          user={user}
          catalog={catalog}
          style={style}
          selectedCats={selectedCats}
          onToggleCat={toggleCat}
          usingCatalogPicker={usingCatalogPicker}
          maxCats={maxCats}
        />

        {/* Pick-by-code (sales/partner/admin only) — feedback 22.05.2026 */}
        {(isPartnerOrSales || isAdmin) &&
          usingCatalogPicker &&
          selectedCats.length > 0 && (
            <section
              className="mt-8 max-w-3xl mx-auto"
              data-testid="pick-by-code-section"
            >
              <div className="border border-white/10 rounded-lg bg-zinc-900/30 p-4 md:p-5 flex items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                  <Code2 className="text-orange-400 mt-0.5 shrink-0" size={20} />
                  <div>
                    <div className="font-outfit font-bold text-white text-sm uppercase tracking-[0.15em]">
                      {t("simulate.specific_profiles_title")}
                    </div>
                    <p className="text-xs text-zinc-400 mt-1 max-w-md leading-relaxed">
                      {t("simulate.specific_profiles_hint")}
                    </p>
                    {pickedCodes.length > 0 && (
                      <div
                        className="mt-2 flex flex-wrap gap-1.5"
                        data-testid="picked-codes-chips"
                      >
                        {pickedCodes.map((c) => (
                          <span
                            key={c}
                            className="font-mono text-[10px] font-bold px-2 py-0.5 rounded bg-orange-500/15 text-orange-300 border border-orange-500/30"
                          >
                            {c}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row items-end sm:items-center gap-2 shrink-0">
                  {pickedCodes.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setPickedCodes([])}
                      className="text-[10px] uppercase tracking-[0.15em] text-zinc-400 hover:text-white"
                      data-testid="clear-picked-codes"
                    >
                      {t("common.clear")}
                    </button>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => setCodePickerOpen(true)}
                    className="border-orange-500/30 text-orange-400 hover:bg-orange-500/10 hover:text-orange-300 hover:border-orange-500/50"
                    data-testid="open-code-picker"
                  >
                    <Code2 size={14} className="mr-1.5" />
                    {pickedCodes.length > 0
                      ? t("simulate.edit_codes_btn", { n: pickedCodes.length })
                      : t("simulate.pick_codes_btn")}
                  </Button>
                </div>
              </div>
            </section>
          )}

        {/* Sprint 2 Vision AI — per-render brief editor (partner/sales/admin).
            Passed as JSON string to /api/simulations via `render_brief`
            multipart field. */}
        {(isPartnerOrSales || isAdmin) && (
          <RenderBriefEditor
            value={renderBrief}
            onChange={setRenderBrief}
            renderMode={effectiveRenderMode}
            lockedCodesCount={pickedCodes.length}
            testId="vision-brief-editor"
          />
        )}

        <FinalActionBar
          variationCount={variationCount}
          onVariationCountChange={setVariationCount}
          onSubmit={submit}
          loading={loading}
          file={file}
          style={style}
          catalog={catalog}
          selectedCats={selectedCats}
          usingCatalogPicker={usingCatalogPicker}
        />
      </main>

      <StickyGenerateBar
        enabled={!!file && !!style && !loading}
        loading={loading}
        variationCount={variationCount}
        onSubmit={submit}
      />
      <ProductCodePickerDialog
        open={codePickerOpen}
        onOpenChange={setCodePickerOpen}
        simType={simType}
        style={style}
        categories={selectedCats}
        initial={pickedCodes}
        onConfirm={(codes) => setPickedCodes(codes)}
      />
    </div>
  );
}
